
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { NodeData } from '../../types';
import { Pencil, Eraser, Trash2, Check, PenTool, RotateCcw, Loader2, RectangleHorizontal, ImageOff } from 'lucide-react';
import { SettingChip, type ChipOption } from './composer/Composer';
import { useFileUrl } from '../../hooks/useFileUrl';
import { uploadWithDeduplication, isFileReference } from '../../utils/fileRegistry';
import { generateSaveFilename, registerLocalFileMapping } from '../../utils/localSave';
import { isLegacyMediaRef } from '../../utils/mediaStorage';

interface SketchNodeProps {
  data: NodeData;
  onDataChange: (newData: NodeData) => void;
  nodeId: string;
  onResize: (id: string, size: { width: number; height: number }) => void;
  userId?: string; // For resolving file:// references
  workspaceName?: string; // For storage paths
  onAutoSaveFile?: (fileData: string | Blob, fileName: string, category?: 'Generated_Images' | 'Videos' | 'Audio' | 'Sketches' | 'Uploads', fileExtension?: string) => Promise<void>;
  isViewOnly?: boolean;
  ownerUserId?: string; // Project owner's userId for cross-user file:// resolution
}

// The proportions a sheet can have, for the ratio chip
const RATIOS: ChipOption[] = ['1:1', '4:3', '3:4', '16:9', '9:16'].map((ratio) => ({ value: ratio, label: ratio }));

export const SketchNode: React.FC<SketchNodeProps> = ({
  data,
  onDataChange,
  nodeId,
  onResize,
  userId,
  workspaceName = 'Untitled',
  onAutoSaveFile,
  isViewOnly = false,
  ownerUserId,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  // Resolve file:// reference to actual URL (with cross-user fallback for shared projects)
  const { url: resolvedImageUrl, isLoading: isResolvingUrl } = useFileUrl(userId, data.imageUrl, workspaceName, ownerUserId);
  
  // If we have an imageURL, we start in View mode. Otherwise, Edit mode.
  // View-only users always start in view mode.
  const [isEditing, setIsEditing] = useState(!data.imageUrl && !isViewOnly);
  
  // Local state for the drawing session (not synced to App until "Done")
  const [tool, setTool] = useState<'pencil' | 'eraser'>('pencil');
  const [aspectRatio, setAspectRatio] = useState(data.sketchConfig?.aspectRatio || '1:1');
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  
  // Constants
  const BASE_WIDTH = 400;
  const HEADER_HEIGHT = 100; // Toolbar + Footer space approximation
  const BRUSH_SIZE = 4;
  const ERASER_SIZE = 20;

  // Calculate dimensions based on ratio
  const getDimensions = useCallback((ratio: string) => {
    const [w, h] = ratio.split(':').map(Number);
    // Fixed width approach
    const width = BASE_WIDTH;
    const height = width * (h / w);
    return { width, height };
  }, []);

  // --- RESIZE HANDLER ---
  useEffect(() => {
    const dims = getDimensions(aspectRatio);
    // We only force resize the parent node if we are actively editing or if it's a fresh mount
    // This keeps the UI snappy.
    onResize(nodeId, { width: dims.width, height: dims.height + HEADER_HEIGHT });
  }, [aspectRatio, nodeId, onResize, getDimensions]);


  // --- CANVAS INITIALIZATION ---
  useEffect(() => {
    if (!isEditing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    const dims = getDimensions(aspectRatio);
    
    // Set resolution
    canvas.width = dims.width;
    canvas.height = dims.height;

    // Fill white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // If there is an existing image, load it onto the canvas
    // Use resolved URL (handles file:// references)
    const imageToLoad = resolvedImageUrl || data.imageUrl;
    if (imageToLoad && !imageToLoad.startsWith('file://')) {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = imageToLoad;
        img.onload = () => {
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        };
    }
  }, [isEditing, aspectRatio, getDimensions, data.imageUrl]);


  // --- DRAWING LOGIC (Native Events for Performance) ---
  const startDrawing = (e: React.PointerEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    isDrawing.current = true;
    canvas.setPointerCapture(e.pointerId);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);

    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = tool === 'eraser' ? ERASER_SIZE : BRUSH_SIZE;
    ctx.strokeStyle = tool === 'eraser' ? '#ffffff' : '#000000';
    // Draw a dot for single click
    ctx.lineTo(x, y); 
    ctx.stroke();
  };

  const draw = (e: React.PointerEvent) => {
    if (!isDrawing.current || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = (e: React.PointerEvent) => {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    if (canvasRef.current) {
        canvasRef.current.releasePointerCapture(e.pointerId);
    }
  };


  // --- ACTIONS ---
  const handleSave = async () => {
      if (!canvasRef.current) return;
      setIsSaving(true);
      
      let imageUrl = canvasRef.current.toDataURL('image/png');
      
      // AUTO-SAVE (both local and Google Drive if enabled). The local copy has the cloud copy's
      // name (category and time), so a lookup by the cloud's name finds it on disk
      const timestamp = Date.now();
      const filename = generateSaveFilename('Sketches', 'png', timestamp);
      if (onAutoSaveFile) {
          try {
              await onAutoSaveFile(imageUrl, filename, 'Sketches', 'png');
          } catch (autoSaveError) {
              console.warn('Sketch auto-save failed:', autoSaveError);
          }
      }
      
      // UPLOAD IMMEDIATELY - convert base64 to file:// reference
      if (userId) {
          try {
              const fileRef = await uploadWithDeduplication(userId, imageUrl, {
                  category: 'Sketches',
                  workspaceName,
                  displayNamePrefix: 'sketch',
                  extension: 'png',
                  nodeId,
                  timestamp,
              });
              // The reference leads to the local copy even when the cloud already had this picture under another name
              if (fileRef.startsWith('file://')) registerLocalFileMapping(fileRef, `Sketches/${filename}`);
              imageUrl = fileRef;
          } catch (uploadError) {
              console.warn('Immediate sketch upload failed, keeping base64:', uploadError);
          }
      }
      
      onDataChange({
          ...data,
          imageUrl,
          sketchConfig: {
              ...data.sketchConfig,
              aspectRatio: aspectRatio,
              brushColor: '#000000',
              brushSize: BRUSH_SIZE,
              backgroundColor: '#ffffff'
          },
          timestamp: Date.now()
      });
      setIsEditing(false);
      setIsSaving(false);
  };

  const handleClear = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (ctx) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
  };

  // --- RENDER ---
  
  // VIEW MODE
  if (!isEditing && data.imageUrl) {
      // Use resolved URL for display
      const displayUrl = resolvedImageUrl || data.imageUrl;
      const canDisplay = displayUrl && !displayUrl.startsWith('file://') && !isLegacyMediaRef(displayUrl);
      
      return (
          <div className="w-full h-full flex flex-col bg-white">
              <div className="flex-1 min-h-0 bg-cream-50 p-2 flex items-center justify-center overflow-hidden">
                  {isResolvingUrl ? (
                      <div className="flex flex-col items-center gap-2 text-ink-faint">
                          <Loader2 size={24} className="animate-spin" />
                          <span className="ns-t-small">Loading...</span>
                      </div>
                  ) : canDisplay ? (
                      <img 
                          src={displayUrl} 
                          className="w-full h-full object-contain shadow-hair bg-white" 
                          draggable={false}
                          alt="Sketch"
                      />
                  ) : (
                      <div className="ns-empty">
                          <span className="ns-mark ns-mark-lg"><ImageOff /></span>
                          <p className="ns-empty-title">Image unavailable</p>
                      </div>
                  )}
              </div>
              {/* The header already says this is a sketch: the row holds the one thing to do with it */}
              {!isViewOnly && (
              <div className="p-3 border-t border-cream-300 flex items-center justify-end bg-white">
                  <button 
                      onClick={() => setIsEditing(true)}
                      className="ns-btn ns-btn-xs ns-btn-tone"
                  >
                      <PenTool size={12} /> Edit
                  </button>
              </div>
              )}
          </div>
      );
  }

  // EDIT MODE
  return (
    <div className="w-full h-full flex flex-col bg-white">
        {/* Toolbar (py-1.5: with the 30px chip the bar is as tall as it was, so as much of the sheet shows) */}
        <div className="px-3 py-1.5 border-b border-cream-300 flex items-center justify-between gap-2 bg-cream-50">
            {/* The tool in hand says so with aria-pressed, which shows it in the node's tone */}
            <div className="flex items-center gap-1">
                <button
                    onClick={() => setTool('pencil')}
                    className="ns-icon-btn ns-icon-btn-sm"
                    aria-pressed={tool === 'pencil'}
                    title="Pencil"
                >
                    <Pencil />
                </button>
                <button
                    onClick={() => setTool('eraser')}
                    className="ns-icon-btn ns-icon-btn-sm"
                    aria-pressed={tool === 'eraser'}
                    title="Eraser"
                >
                    <Eraser />
                </button>
            </div>

            {/* The ratio is a chip that opens the menu, as in the Image and Video nodes */}
            <SettingChip
                icon={<RectangleHorizontal size={14} />}
                label="Aspect ratio"
                value={aspectRatio}
                options={RATIOS}
                onChange={setAspectRatio}
            />

            <button
                onClick={handleClear}
                className="ns-icon-btn ns-icon-btn-sm ns-icon-btn-danger"
                title="Clear all"
            >
                <RotateCcw />
            </button>
        </div>

        {/* Canvas */}
        <div className="flex-1 bg-cream-50 relative overflow-hidden touch-none cursor-crosshair flex items-center justify-center">
             {/* We center the canvas if aspect ratio leaves gaps, but our resize logic tries to match perfectly */}
            <canvas
                ref={canvasRef}
                className="bg-white shadow-hair block"
                onPointerDown={startDrawing}
                onPointerMove={draw}
                onPointerUp={stopDrawing}
                onPointerLeave={stopDrawing}
            />
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-cream-300 bg-white flex items-center justify-between">
            <button 
                onClick={() => {
                    // Cancel changes logic? For now just re-save current state or revert.
                    // If we have an image, go back to it. If not, delete?
                    if (data.imageUrl) setIsEditing(false);
                    else {
                        // If it's new and they cancel, maybe clear? 
                        // Let's just stay in edit mode but show a warning or handle delete in parent.
                        setIsEditing(false);
                    }
                }}
                className="ns-btn ns-btn-xs ns-btn-soft"
            >
                Cancel
            </button>
            
            <button 
                onClick={handleSave}
                disabled={isSaving}
                className="ns-btn ns-btn-xs ns-btn-primary"
            >
                {isSaving ? (
                    <>
                        <Loader2 size={12} className="animate-spin" /> Saving...
                    </>
                ) : (
                    <>
                        <Check size={12} /> Done
                    </>
                )}
            </button>
        </div>
    </div>
  );
};
